import { useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { styles } from "../styles";
import { isAdminTier } from "../utils/capabilities";
import BackButton from "../components/BackButton";

// Canonical key for the per-pair disconnect map on accounts + buildings.
// Mirror of the server-side `pairKey` in functions/index.js — same sort +
// join so disconnectedPairs[pairKey(A, B)] === disconnectedPairs[pairKey(B, A)].
const pairKey = (a, b) => [a, b].sort().join("__");

/**
 * Read-only directory of every org this one is linked to. We aggregate the
 * denormalized `linkedOrgInfo` map across the calling org's shared accounts
 * + buildings to derive:
 *   - one row per partner org
 *   - their name + type (from linkedOrgInfo, fallback to a truncated orgId)
 *   - count of accounts + buildings shared with that partner
 *
 * Pre-deploy links won't have linkedOrgInfo yet (the denormalization landed
 * with the multi-contractor dropdown work). Those show as a short orgId
 * fallback; re-linking the building/account refreshes the name.
 */
export default function ConnectionsPage({
  customersRaw = [],
  locationsRaw = [],
  orgId,
  orgType,
  role,
  actions,
  onBack,
}) {
  const canUnlink = isAdminTier(role);
  const [unlinkingOrg, setUnlinkingOrg] = useState(null);
  // Keyed by the partner orgId we last acted on — otherwise a single
  // shared string would render under every connection row, making it
  // look like every partner just got disconnected.
  const [unlinkMsgByOrg, setUnlinkMsgByOrg] = useState({});

  // Disconnect this partner from every customer + building we share with
  // them. The server figures out the correct flag key per-doc: if we OWN
  // the doc, it flags the partner (kicks them out); if we don't, it flags
  // ourselves (self-leave). Either way the downstream effect is the
  // same — no new work flows between the two orgs on this share.
  const handleUnlink = async (c) => {
    const accCount = c.accountCount;
    const bldCount = c.buildingCount;
    if (accCount === 0 && bldCount === 0) {
      window.alert(
        `No shared records found with ${c.name || "this partner"}.`
      );
      return;
    }
    const ok = window.confirm(
      `End relationship with ${c.name || "this partner"}?\n\n` +
        `This disconnects ${accCount} customer${
          accCount === 1 ? "" : "s"
        } ` +
        `and ${bldCount} building${bldCount === 1 ? "" : "s"} you share.\n\n` +
        `• Existing work both sides have already shared — service tickets, ` +
        `invoices, equipment, service records — stays visible on both sides.\n` +
        `• NEW work either side creates after this won't flow across: no new ` +
        `service tickets, invoices, quotes, equipment, or service records.\n\n` +
        `Re-linking later via a fresh share code clears the disconnect ` +
        `and catches both sides up on records created during the gap.`
    );
    if (!ok) return;
    setUnlinkingOrg(c.orgId);
    setUnlinkMsgByOrg((prev) => ({ ...prev, [c.orgId]: "" }));
    try {
      // Runs server-side because firestore.rules' memberOrgsUnchanged
      // guard blocks direct client writes to memberOrgs. The function
      // verifies the caller owns those customers/buildings first.
      // Only send the OWNED ids — the function would skip the others
      // anyway, but trimming on the client makes the request smaller
      // and the function's success message accurate.
      const fn = httpsCallable(functions, "unlinkPartner");
      const res = await fn({
        partnerOrgId: c.orgId,
        // Send EVERY shared doc — server picks the right flag key per-doc
        // based on whether we own it. Sending only owned would block the
        // non-owner self-leave path.
        accountIds: [...c.accountIds],
        buildingIds: c.buildingDocs.map((b) => b.id),
      });
      setUnlinkMsgByOrg((prev) => ({
        ...prev,
        [c.orgId]: res.data?.message || `Disconnected ${c.name || c.orgId}.`,
      }));
    } catch (err) {
      setUnlinkMsgByOrg((prev) => ({
        ...prev,
        [c.orgId]: `Couldn't disconnect: ${err.message}`,
      }));
    } finally {
      setUnlinkingOrg(null);
    }
  };
  const connections = useMemo(() => {
    const byOrg = new Map();
    const ensure = (otherOrgId, infoMaybe) => {
      if (!byOrg.has(otherOrgId)) {
        byOrg.set(otherOrgId, {
          orgId: otherOrgId,
          name: null,
          type: null,
          linkedAt: null,
          // accountIds / buildingDocs are EVERY shared doc (for display
          // count purposes). ownedAccountIds / ownedBuildingDocs are
          // narrowed to docs THIS org owns — that's the subset the
          // unlinkPartner Cloud Function will actually act on, since a
          // non-owner can't remove other partners from a doc.
          accountIds: new Set(),
          buildingDocs: [],
          ownedAccountIds: new Set(),
          ownedBuildingDocs: [],
          disconnectedAccountCount: 0,
          disconnectedBuildingCount: 0,
        });
      }
      const row = byOrg.get(otherOrgId);
      if (infoMaybe) {
        if (!row.name && infoMaybe.name) row.name = infoMaybe.name;
        if (!row.type && infoMaybe.type) row.type = infoMaybe.type;
        if (!row.linkedAt && infoMaybe.linkedAt) row.linkedAt = infoMaybe.linkedAt;
      }
      return row;
    };

    // A (me, partner) pair is disconnected on a doc if
    // disconnectedPairs[pairKey(me, partner)] is set. The pair-keyed map
    // is symmetric (server sorts orgIds), so it doesn't matter whether
    // I initiated or my partner did — the same key flips on for both.
    for (const a of customersRaw) {
      const others = (a.memberOrgs || []).filter((o) => o && o !== orgId);
      const pairs = a.disconnectedPairs || {};
      for (const o of others) {
        const row = ensure(o, a.linkedOrgInfo?.[o]);
        row.accountIds.add(a.id);
        if (a.ownerOrgId === orgId) row.ownedAccountIds.add(a.id);
        if (pairs[pairKey(orgId, o)]) row.disconnectedAccountCount++;
      }
    }
    for (const b of locationsRaw) {
      const others = (b.memberOrgs || []).filter((o) => o && o !== orgId);
      const pairs = b.disconnectedPairs || {};
      for (const o of others) {
        const row = ensure(o, b.linkedOrgInfo?.[o]);
        row.buildingDocs.push(b);
        if (b.ownerOrgId === orgId) row.ownedBuildingDocs.push(b);
        if (pairs[pairKey(orgId, o)]) row.disconnectedBuildingCount++;
      }
    }

    return [...byOrg.values()]
      .filter((r) => {
        // Hide same-type partners. A contractor seeing another contractor
        // listed here would invite them to "Unlink" the co-contractor —
        // which would set a (C1, C2) disconnect pair and silently break
        // the shared-notes flow at the facility they both serve. Same goes
        // for a facility seeing another facility under a shared contractor:
        // they aren't each other's counterparty, they're peer customers of
        // the same contractor and shouldn't manage each other's links.
        //
        // Viewer-side guard: if our own orgType is missing (stale auth
        // claim), show everything so the page isn't blank. Stale claim is
        // a UX issue, not a privacy leak — the user is looking at their
        // OWN connections.
        if (!orgType) return true;
        // Partner-side guard: if the partner's denormalized type is
        // missing/empty (pre-denorm doc, missing on the partner's org
        // record, etc.), HIDE them — fail closed. The previous fail-open
        // behavior leaked same-type peer orgs (facility-to-facility under
        // a shared contractor). Re-linking via fresh share code refreshes
        // linkedOrgInfo with the partner's type and brings legitimate
        // cross-type partners back on.
        if (!r.type) return false;
        return r.type !== orgType;
      })
      .map((r) => ({
        ...r,
        accountCount: r.accountIds.size,
        buildingCount: r.buildingDocs.length,
        ownedAccountCount: r.ownedAccountIds.size,
        ownedBuildingCount: r.ownedBuildingDocs.length,
        // "Disconnected" if EVERY shared doc (owned or not) has either
        // side's disconnect flag set. unlinkPartner stamps all shared
        // docs in one shot, so all-or-nothing is the common case.
        isDisconnected:
          r.accountIds.size + r.buildingDocs.length > 0 &&
          r.disconnectedAccountCount === r.accountIds.size &&
          r.disconnectedBuildingCount === r.buildingDocs.length,
      }))
      .sort((a, b) => (a.name || a.orgId).localeCompare(b.name || b.orgId));
  }, [customersRaw, locationsRaw, orgId, orgType]);

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (partnerOrgId) =>
    setExpanded((prev) => ({ ...prev, [partnerOrgId]: !prev[partnerOrgId] }));

  // Flag is keyed by the RECIPIENT orgId (= the user toggling the box wants
  // themselves alerted). Dashboard reads the same key.
  const toggleBuildingNotify = async (building) => {
    if (!actions?.updateLocation || !orgId) return;
    const current =
      building.notifyOnEquipmentStatusFor?.[orgId] === true;
    await actions.updateLocation(building.id, {
      notifyOnEquipmentStatusFor: {
        ...(building.notifyOnEquipmentStatusFor || {}),
        [orgId]: !current,
      },
    });
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Connections</h2>
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 8px" }}>
        Other organizations linked to yours via share codes. Linking is per
        building or per account; one partner may be on many of either.
      </p>

      {connections.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>
          No connections yet. Share a building or customer code with a partner
          to start linking.
        </p>
      ) : (
        connections.map((c) => (
          <div key={c.orgId} style={styles.card}>
            <div
              onClick={() => toggleExpanded(c.orgId)}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontFamily: "monospace",
                  width: "12px",
                }}
              >
                {expanded[c.orgId] ? "▼" : "▶"}
              </span>
              <strong style={{ fontSize: "14px" }}>
                {c.name || `Org ${c.orgId.slice(0, 8)}`}
              </strong>
              {c.type && (
                <span
                  style={{
                    fontSize: "11px",
                    padding: "1px 6px",
                    background: "#dde7f4",
                    borderRadius: "4px",
                  }}
                >
                  {c.type === "mechanical" ? "Contractor" : "Facility"}
                </span>
              )}
              {c.isDisconnected && (
                <span
                  style={{
                    fontSize: "11px",
                    padding: "1px 6px",
                    background: "#f3e0e0",
                    color: "#8a3a3a",
                    borderRadius: "4px",
                  }}
                  title="New work won't flow to this partner. Existing records still shared. Re-link via a fresh share code to resume."
                >
                  Disconnected
                </span>
              )}
            </div>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
              {c.accountCount} customer{c.accountCount === 1 ? "" : "s"}
              {" · "}
              {c.buildingCount} building
              {c.buildingCount === 1 ? "" : "s"}
              {c.linkedAt
                ? ` · linked ${new Date(c.linkedAt).toLocaleDateString()}`
                : ""}
            </div>
            {canUnlink && (
              <div style={{ marginTop: "6px" }}>
                <button
                  type="button"
                  style={{
                    ...styles.button,
                    margin: 0,
                    fontSize: "12px",
                    background: "#fee",
                    color: "#a00",
                    borderColor: "#f3c0c0",
                  }}
                  onClick={() => handleUnlink(c)}
                  disabled={unlinkingOrg === c.orgId || c.isDisconnected}
                  title={
                    c.isDisconnected
                      ? "Already disconnected — re-link via a fresh share code to resume"
                      : "End the relationship: stops new work flowing between the two of you. Existing records stay visible on both sides."
                  }
                >
                  {unlinkingOrg === c.orgId
                    ? "Disconnecting…"
                    : c.isDisconnected
                    ? "Disconnected"
                    : "Unlink"}
                </button>
                {unlinkMsgByOrg[c.orgId] && unlinkingOrg === null && (
                  <span style={{ fontSize: "12px", marginLeft: "8px", color: "#666" }}>
                    {unlinkMsgByOrg[c.orgId]}
                  </span>
                )}
              </div>
            )}
            {!c.name && (
              <p
                style={{
                  fontSize: "11px",
                  color: "#a33",
                  margin: "4px 0 0",
                }}
              >
                Name unavailable — this link was created before names were
                stored. Re-linking via share code will refresh it.
              </p>
            )}
            {expanded[c.orgId] &&
              c.type !== "mechanical" &&
              actions?.updateLocation &&
              c.buildingDocs.length > 0 && (
                <div style={{ marginTop: "10px" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "4px",
                    }}
                  >
                    Alert me when equipment goes yellow or red at:
                  </div>
                  {c.buildingDocs
                    .slice()
                    .sort((a, b) =>
                      (a.name || "").localeCompare(b.name || "")
                    )
                    .map((b) => (
                      <label
                        key={b.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "4px 0",
                          fontSize: "13px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            b.notifyOnEquipmentStatusFor?.[orgId] === true
                          }
                          onChange={() => toggleBuildingNotify(b)}
                        />
                        {b.name || `Building ${b.id.slice(0, 8)}`}
                      </label>
                    ))}
                </div>
              )}
          </div>
        ))
      )}
    </>
  );
}
